/**
 * Pagina Cassa (POS) — `/cassa`.
 *
 * Layout 2-column (mobile collapses to stacked):
 *   - LEFT  → ricerca prodotti per nome o barcode/SKU, lista risultati con +Aggiungi
 *   - RIGHT → conto corrente con +/- per quantità, sconto %/fisso, totale, checkout
 *
 * Backend:
 *   GET  /api/inventory?q=<query>   → search
 *   POST /api/cash/checkout         → decrement stock + audit log + persist receipt
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Search, Plus, Minus, Trash2, Equal, Tag, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

import Header from '../components/Header';
import BottomNav from '../components/BottomNav';
import BackButton from '../components/BackButton';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => `€ ${Number(n || 0).toFixed(2).replace('.', ',')}`;

export default function CassaPage() {
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [cart, setCart] = useState([]); // [{id, nome, prezzo, qty, barcode}]
  const [discountMode, setDiscountMode] = useState('percent'); // percent | fixed
  const [discountValue, setDiscountValue] = useState(0);
  const [showDiscount, setShowDiscount] = useState(false);
  const [showTotal, setShowTotal] = useState(false);
  const [busy, setBusy] = useState(false);

  // Load inventory once for fast live search.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // v1.8.3 — backend now paginates /inventory (default 50). Request the
        // full page (max=500) since Cassa filters client-side. Larger shops
        // should ideally move to server-side search — tracked as backlog.
        const res = await axios.get(`${API}/inventory?limit=500`, { withCredentials: true });
        if (alive) setAllItems(res.data || []);
      } catch {
        if (alive) toast.error('Errore caricamento prodotti');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allItems.slice(0, 20);
    return allItems.filter((p) =>
      (p.nome || '').toLowerCase().includes(q)
      || (p.barcode || '').toLowerCase().includes(q)
      || (p.sku || '').toLowerCase().includes(q),
    ).slice(0, 30);
  }, [allItems, query]);

  const addToCart = (item) => {
    setCart((prev) => {
      const existing = prev.find((c) => c.id === item.id);
      if (existing) {
        return prev.map((c) => (c.id === item.id ? { ...c, qty: c.qty + 1 } : c));
      }
      return [...prev, { id: item.id, nome: item.nome, prezzo: Number(item.prezzo || 0), qty: 1, barcode: item.barcode }];
    });
  };

  const bumpQty = (id, delta) => {
    setCart((prev) => prev
      .map((c) => (c.id === id ? { ...c, qty: c.qty + delta } : c))
      .filter((c) => c.qty > 0));
  };

  const clearItem = (id) => setCart((prev) => prev.filter((c) => c.id !== id));

  const subtotal = useMemo(() => cart.reduce((s, c) => s + c.prezzo * c.qty, 0), [cart]);
  const discountAmount = useMemo(() => {
    if (discountMode === 'percent') return subtotal * (Math.max(0, Math.min(100, Number(discountValue || 0))) / 100);
    return Math.max(0, Number(discountValue || 0));
  }, [discountMode, discountValue, subtotal]);
  const total = Math.max(0, subtotal - discountAmount);

  const handleCheckout = async () => {
    if (!cart.length) return;
    setBusy(true);
    try {
      await axios.post(`${API}/cash/checkout`, {
        lines: cart.map((c) => ({ product_id: c.id, quantita: c.qty, prezzo_unitario: c.prezzo })),
        sconto_percent: discountMode === 'percent' ? Number(discountValue || 0) : 0,
        sconto_fisso: discountMode === 'fixed' ? Number(discountValue || 0) : 0,
      }, { withCredentials: true });
      toast.success(`Vendita registrata: ${fmt(total)}`);
      setCart([]);
      setDiscountValue(0);
      setShowTotal(false);
      // Refresh stock counters
      const res = await axios.get(`${API}/inventory?limit=500`, { withCredentials: true });
      setAllItems(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore checkout');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="cassa-page">
      <Header />
      <main className="max-w-6xl mx-auto px-4 py-4">
        <BackButton fallback="/dashboard" className="mb-3" />
        <h1 className="font-outfit font-bold text-2xl sm:text-3xl text-slate-800 mb-1">Cassa</h1>
        <p className="text-sm text-slate-500 mb-4">Cerca prodotti, costruisci il conto e applica eventuali sconti.</p>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* LEFT — search & catalog */}
          <section className="lg:col-span-3 bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="p-3 border-b border-slate-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Cerca per nome o codice prodotto…"
                  autoFocus
                  className="pl-9 rounded-xl h-11"
                  data-testid="cassa-search-input"
                />
              </div>
            </div>
            <ul className="max-h-[60vh] overflow-y-auto">
              {loading ? (
                <li className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></li>
              ) : results.length === 0 ? (
                <li className="text-center py-10 text-slate-500 text-sm">Nessun prodotto trovato</li>
              ) : (
                results.map((p) => (
                  <li key={p.id} className="flex items-center gap-3 px-4 py-3 border-b border-slate-50 hover:bg-slate-50/60" data-testid={`cassa-product-${p.id}`}>
                    {p.immagini?.[0] ? (
                      <img src={p.immagini[0]} alt="" className="w-10 h-10 rounded-lg object-cover bg-slate-100" />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-slate-100" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">{p.nome}</div>
                      <div className="text-xs text-slate-500 flex gap-2">
                        {p.barcode && <span>{p.barcode}</span>}
                        <span>· {fmt(p.prezzo)}</span>
                        <span>· qty {p.quantita_magazzino ?? 0}</span>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700 text-white h-8"
                      onClick={() => addToCart(p)}
                      data-testid={`cassa-add-${p.id}`}
                    >
                      <Plus className="w-4 h-4 mr-1" />Aggiungi
                    </Button>
                  </li>
                ))
              )}
            </ul>
          </section>

          {/* RIGHT — conto */}
          <section className="lg:col-span-2 bg-white rounded-2xl shadow-sm overflow-hidden" data-testid="cassa-cart">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <h2 className="font-outfit font-semibold text-slate-800">Conto</h2>
              <span className="text-xs text-slate-500" data-testid="cart-count">{cart.length} righe</span>
            </div>

            <ul className="divide-y divide-slate-50 max-h-[40vh] overflow-y-auto">
              {cart.length === 0 ? (
                <li className="px-4 py-10 text-center text-sm text-slate-500">Conto vuoto</li>
              ) : (
                cart.map((c) => (
                  <li key={c.id} className="px-3 py-2 flex items-center gap-2" data-testid={`cart-line-${c.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">{c.nome}</div>
                      <div className="text-xs text-slate-500">{fmt(c.prezzo)} × {c.qty} = <strong>{fmt(c.prezzo * c.qty)}</strong></div>
                    </div>
                    <button onClick={() => bumpQty(c.id, -1)} className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-slate-200" aria-label="Diminuisci" data-testid={`cart-dec-${c.id}`}>
                      <Minus className="w-4 h-4 mx-auto" />
                    </button>
                    <span className="w-6 text-center text-sm font-semibold">{c.qty}</span>
                    <button onClick={() => bumpQty(c.id, 1)} className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-slate-200" aria-label="Aumenta" data-testid={`cart-inc-${c.id}`}>
                      <Plus className="w-4 h-4 mx-auto" />
                    </button>
                    <button onClick={() => clearItem(c.id)} className="w-7 h-7 rounded-lg text-red-500 hover:bg-red-50" aria-label="Rimuovi" data-testid={`cart-rm-${c.id}`}>
                      <Trash2 className="w-4 h-4 mx-auto" />
                    </button>
                  </li>
                ))
              )}
            </ul>

            <div className="px-4 py-3 border-t border-slate-100 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Subtotale</span>
                <span className="font-medium" data-testid="subtotal">{fmt(subtotal)}</span>
              </div>
              {discountAmount > 0 && (
                <div className="flex items-center justify-between text-sm text-red-600">
                  <span>Sconto {discountMode === 'percent' ? `${discountValue}%` : ''}</span>
                  <span data-testid="discount-amount">- {fmt(discountAmount)}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-lg pt-2 border-t border-slate-100">
                <span className="font-semibold">Totale</span>
                <span className="font-bold text-slate-900" data-testid="total">{fmt(total)}</span>
              </div>

              <div className="grid grid-cols-3 gap-2 pt-3">
                <Button
                  variant="outline"
                  className="rounded-xl flex items-center gap-1"
                  onClick={() => setShowDiscount(true)}
                  data-testid="open-discount"
                >
                  <Tag className="w-4 h-4" />Sconto
                </Button>
                <Button
                  variant="outline"
                  className="rounded-xl flex items-center gap-1"
                  onClick={() => setShowTotal(true)}
                  data-testid="open-total"
                  disabled={!cart.length}
                >
                  <Equal className="w-4 h-4" />Totale
                </Button>
                <Button
                  className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1"
                  onClick={handleCheckout}
                  disabled={!cart.length || busy}
                  data-testid="cassa-checkout"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  Incassa
                </Button>
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* Discount modal */}
      <Dialog open={showDiscount} onOpenChange={setShowDiscount}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Applica sconto</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Button
                variant={discountMode === 'percent' ? 'default' : 'outline'}
                className={discountMode === 'percent' ? 'bg-blue-600 text-white' : ''}
                onClick={() => setDiscountMode('percent')}
                data-testid="discount-mode-percent"
              >% Percentuale</Button>
              <Button
                variant={discountMode === 'fixed' ? 'default' : 'outline'}
                className={discountMode === 'fixed' ? 'bg-blue-600 text-white' : ''}
                onClick={() => setDiscountMode('fixed')}
                data-testid="discount-mode-fixed"
              >€ Fisso</Button>
            </div>
            <div>
              <Label htmlFor="discount-value">{discountMode === 'percent' ? 'Percentuale' : 'Importo (€)'}</Label>
              <Input
                id="discount-value"
                type="number"
                min={0}
                value={discountValue}
                onChange={(e) => setDiscountValue(e.target.value)}
                className="mt-1 rounded-xl"
                data-testid="discount-value-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDiscountValue(0); setShowDiscount(false); }}>Rimuovi</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setShowDiscount(false)} data-testid="discount-apply">
              Applica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Total modal (= button) */}
      <Dialog open={showTotal} onOpenChange={setShowTotal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Totale conto</DialogTitle>
          </DialogHeader>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between"><span>Subtotale</span><span>{fmt(subtotal)}</span></div>
            {discountAmount > 0 && (
              <div className="flex justify-between text-red-600"><span>Sconto</span><span>- {fmt(discountAmount)}</span></div>
            )}
            <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2"><span>Totale netto</span><span>{fmt(total)}</span></div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowTotal(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
}
